// Onglet « Temps prévu / réel » de la page du chantier (31/10/2026) : heures prévues des étapes faites comparées au temps déclaré,
// au niveau où la donnée existe (chantier, poste, ouvrage type). Le temps déclaré pour tout le chantier n'est JAMAIS réparti : l'écran
// le dit. Encadrement (responsable, référent, chargé d'affaires, administrateur) : la comparaison ; technicien : son propre temps.
// Aucun montant, aucun nom de personne, aucun pourcentage décoratif : des heures, et l'écart en heures et en pourcentage.
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, Scale } from 'lucide-react';
import { api } from '../../../lib/api.ts';
import { nombre, pluriel } from '../../../lib/format.ts';
import { Alerte, Badge, Chargement, EtatErreur, EtatVide, Panneau } from '../../../components/ui.tsx';
import { heures } from './commun.tsx';
import { jourCourt } from './jours.ts';

interface Mesure {
  prevues: string; prevues_faites: string; etapes: number; faites: number; sans_temps: number; faites_sans_temps: number; faites_retirees: number;
  toutes_faites: boolean; reel: string | null; ecart: string | null; ecart_pct: string | null; comparable: boolean; motif: string | null;
}
interface Comparaison {
  niveau: 'aucun' | 'poste' | 'chantier';
  chantier: Mesure;
  bureau: { prevues: string; postes: number };
  temps_non_reparti: string;
  temps_total: string | null;
  postes: (Mesure & { id: string; label: string; place: 'site' | 'bureau'; declare_sur_poste: string | null })[];
  ouvrages: (Mesure & { work_item_id: string; code: string | null; label: string | null; unite: string | null; quantite: string | null;
    prevu_par_unite: string | null; reel_par_unite: string | null })[];
}
type Vue =
  | { vue: 'complete'; chantier: { status: string }; reglages: { temps: boolean }; declarations: number; comparaison: Comparaison | null }
  | { vue: 'personnelle'; chantier: { status: string }; reglages: { temps: boolean };
      mon_temps: { total: string | null; jours: { jour: string | null; heures: string; poste: string | null }[] } | null };

/** « + 1,5 h (+ 12,5 %) — plus long que prévu » ; jamais une couleur seule. */
export function Ecart({ m }: { m: Pick<Mesure, 'ecart' | 'ecart_pct'> }) {
  if (m.ecart === null) return null;
  const v = Number(m.ecart);
  const signe = v > 0 ? '+ ' : v < 0 ? '− ' : '';
  const abs = (x: string) => nombre(x.replace('-', ''), 2);
  return (
    <span className="tpr-ecart chiffre" data-sens={v > 0 ? 'plus' : v < 0 ? 'moins' : 'egal'}>
      {signe}{abs(m.ecart)} h{m.ecart_pct !== null ? ` (${signe}${nombre(m.ecart_pct.replace('-', ''), 1)} %)` : ''}
      <span className="petit secondaire"> — {v > 0 ? 'plus long que prévu' : v < 0 ? 'plus court que prévu' : 'conforme au prévu'}</span>
    </span>
  );
}

function TroisChiffres({ m }: { m: Mesure }) {
  return (
    <dl className="ch-chiffres av-mesures">
      <div>
        <dt><CheckCircle2 aria-hidden /> Prévu des étapes faites</dt>
        <dd className="chiffre">{heures(m.prevues_faites)} sur {heures(m.prevues)}</dd>
        <span className="petit secondaire">{m.faites} étape{m.faites > 1 ? 's' : ''} faite{m.faites > 1 ? 's' : ''} sur {m.etapes}</span>
      </div>
      <div>
        <dt><Clock aria-hidden /> Temps déclaré</dt>
        <dd className="chiffre">{m.reel !== null ? heures(m.reel) : 'Aucun'}</dd>
        <span className="petit secondaire">{m.reel !== null ? 'Déclaré par les techniciens' : 'Rien d’inventé : vide tant que rien n’est déclaré'}</span>
      </div>
      <div>
        <dt><Scale aria-hidden /> Écart</dt>
        <dd className="chiffre">{m.ecart !== null ? <Ecart m={m} /> : 'Non calculé'}</dd>
        {m.ecart === null && m.motif && <span className="petit secondaire">{m.motif}</span>}
      </div>
    </dl>
  );
}

function Avis({ m, bureau }: { m: Mesure; bureau?: Comparaison['bureau'] }) {
  const avis = [
    m.sans_temps ? `${pluriel(m.sans_temps, 'étape')} sans temps prévu (« à renseigner ») : hors des heures prévues et de l’écart — le temps déclaré peut les comprendre.` : null,
    m.faites_retirees ? `${pluriel(m.faites_retirees, 'étape faite puis retirée', 'étapes faites puis retirées')} : hors du prévu, à arbitrer.` : null,
    bureau && bureau.postes ? `${heures(bureau.prevues)} prévues au bureau (${pluriel(bureau.postes, 'poste')}) : comptées à part, le temps déclaré est celui passé sur le chantier.` : null,
  ].filter(Boolean) as string[];
  if (!avis.length) return null;
  return <ul className="petit secondaire av-avis">{avis.map((x) => <li key={x}>{x}</li>)}</ul>;
}

/** Une ligne de comparaison (poste ou ouvrage type). */
function Ligne({ titre, sous, m, extra }: { titre: string; sous?: string | null; m: Mesure; extra?: ReactNode }) {
  return (
    <li className="tpr-ligne">
      <div className="tpr-ligne-titre">
        <span className="ch-poste-titre">{titre}</span>
        {sous && <span className="petit discret">{sous}</span>}
      </div>
      <p className="petit chiffre tpr-ligne-chiffres">
        Prévu des étapes faites : <strong>{heures(m.prevues_faites)}</strong> sur {heures(m.prevues)}
        {' · '}Déclaré : <strong>{m.reel !== null ? heures(m.reel) : 'aucun'}</strong>
      </p>
      {m.ecart !== null ? <p className="petit tpr-ligne-ecart">Écart : <Ecart m={m} /></p>
        : m.motif ? <p className="petit secondaire tpr-motif">{m.motif}</p> : null}
      {extra}
      {m.sans_temps > 0 && <p className="petit secondaire tpr-motif">{pluriel(m.sans_temps, 'étape')} sans temps prévu, hors de l’écart.</p>}
    </li>
  );
}

export function PanneauTempsPrevuReel({ chantierId }: { chantierId: string }) {
  const q = useQuery({ queryKey: ['chantier-temps-prevu-reel', chantierId], queryFn: () => api.get<Vue>(`/api/chantiers/${chantierId}/temps-prevu-reel`) });
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const v = q.data!;
  if (v.vue === 'personnelle') {
    if (!v.mon_temps) {
      return <Panneau titre="Mon temps sur ce chantier"><EtatVide icone={<Clock />} titre="Déclaration du temps désactivée"
        texte="Votre entreprise a désactivé la déclaration du temps passé : rien n’est affiché ici." /></Panneau>;
    }
    return (
      <Panneau titre="Mon temps sur ce chantier">
        <div className="pile">
          <p className="chiffre tpr-total">{v.mon_temps.total !== null ? <>Total déclaré : <strong>{heures(v.mon_temps.total)}</strong></> : 'Aucun temps déclaré.'}</p>
          {v.mon_temps.jours.length > 0 && (
            <ul className="tpr-jours">
              {v.mon_temps.jours.map((j, i) => (
                <li key={`${j.jour}-${i}`}><span>{j.jour ? jourCourt(j.jour) : '—'}{j.poste ? <span className="petit discret"> — {j.poste}</span> : null}</span>
                  <strong className="chiffre">{heures(j.heures)}</strong></li>
              ))}
            </ul>
          )}
          <p className="petit secondaire">Vous voyez votre seul temps. La comparaison avec le temps prévu est réservée au responsable du chantier, au référent et au chargé d’affaires.</p>
        </div>
      </Panneau>
    );
  }
  const c = v.comparaison;
  if (!c) return <Alerte ton="info">Le chantier est en préparation : la comparaison commence au lancement, avec les premières coches et le premier temps déclaré.</Alerte>;
  const actifs = c.postes;
  // Postes sur site et ouvrages que le temps déclaré pour tout le chantier empêche de comparer (jamais réparti).
  const bloques = c.postes.filter((p) => p.place === 'site' && p.reel === null).length + c.ouvrages.filter((o) => o.reel === null).length;
  return (
    <div className="pile-l">
      <Panneau titre="Chantier — postes sur site">
        <div className="pile">
          <TroisChiffres m={c.chantier} />
          <Avis m={c.chantier} bureau={c.bureau} />
          {!v.reglages.temps && <p className="petit secondaire" style={{ margin: 0 }}>La déclaration du temps est désactivée par l’entreprise : seul le temps déjà déclaré est compté.</p>}
        </div>
      </Panneau>

      {c.niveau === 'chantier' && bloques === 0 && (
        <p className="petit secondaire" style={{ margin: 0 }}>
          Le temps est déclaré pour tout le chantier ({heures(c.temps_non_reparti)} sans poste précis) : il se rattache ici aux postes et à l’ouvrage
          parce que le chantier {c.postes.filter((p) => p.place === 'site').length === 1 ? 'n’a qu’un poste sur site' : 'ne comporte qu’un seul ouvrage type'}.
        </p>
      )}
      {c.niveau === 'chantier' && bloques > 0 && (
        <Alerte ton="info" titre="Temps déclaré pour tout le chantier">
          {heures(c.temps_non_reparti)} ont été déclarées sans poste précis. TRAME ne les répartit jamais entre les postes ni entre les ouvrages :
          la comparaison se fait au niveau du chantier. Un poste ou un ouvrage type se compare seulement quand le temps est déclaré sur lui,
          quand le chantier n’a qu’un poste sur site, ou quand tous ses postes sur site viennent du même ouvrage type.
        </Alerte>
      )}
      {c.niveau === 'aucun' && <Alerte ton="info">Aucun temps déclaré sur ce chantier pour l’instant : rien à comparer.</Alerte>}

      {c.ouvrages.length > 0 && (
        <Panneau serre titre={`Par ouvrage type (${c.ouvrages.length})`}>
          <ul className="tpr-liste">
            {c.ouvrages.map((o) => (
              <Ligne key={o.work_item_id} titre={o.label ?? 'Ouvrage type'} m={o}
                sous={[o.code, o.quantite !== null ? `${nombre(o.quantite, 4)} ${o.unite ?? 'u'} posé${Number(o.quantite) > 1 ? 's' : ''}` : 'quantité non retrouvée'].filter(Boolean).join(' — ')}
                extra={o.prevu_par_unite !== null ? (
                  <p className="petit chiffre tpr-ligne-chiffres">Par {o.unite ?? 'unité'} : prévu <strong>{heures(o.prevu_par_unite)}</strong>
                    {' · '}réel {o.reel_par_unite !== null ? <strong>{heures(o.reel_par_unite)}</strong> : <span className="secondaire">non calculé (étapes non toutes faites ou temps non rattachable)</span>}</p>
                ) : null} />
            ))}
          </ul>
        </Panneau>
      )}

      <Panneau serre titre={`Par poste (${actifs.length})`}>
        {actifs.length === 0 ? <EtatVide titre="Aucun poste" /> : (
          <ul className="tpr-liste">
            {actifs.map((p) => (
              <Ligne key={p.id} titre={p.label} m={p}
                sous={p.place === 'bureau' ? 'Au bureau' : p.declare_sur_poste !== null && p.reel === null ? `${heures(p.declare_sur_poste)} déclarées sur ce poste (incomplet)` : null}
                extra={p.place === 'bureau' ? <Badge ton="info" icone={false}>Au bureau</Badge> : null} />
            ))}
          </ul>
        )}
      </Panneau>
      <p className="petit discret">L’écart se lit contre les heures prévues des étapes FAITES (une étape faite aux trois quarts compte les trois quarts de ses heures).
        Le temps déclaré sert au suivi du chantier ; ce n’est pas le décompte légal du temps de travail.</p>
    </div>
  );
}
